import { useState, useCallback, useEffect } from 'react';
import { getCurrentWeekKey, getWeekStartDateFromKey } from '../utils/weekKeyUtils';
import { 
  getSnapshotsByWeek, 
  saveSnapshot as saveSnapshotToSupabase, 
  deleteSnapshotsByWeek,
  getAllWeekKeys 
} from '../supabase/snapshotService';
import { logger } from '../utils/logger';

// Type for snapshot data structure
export type SnapshotData = Record<string, unknown>;

// Type for a single snapshot (all section data for a week)
export interface DashboardSnapshot {
  week: string; // e.g. '2025-W49'
  data: SnapshotData; // sectionKey -> sectionData
  timestamp: string;
}


async function getAllSnapshots(): Promise<DashboardSnapshot[]> {
  try {
    // Get all week keys from Supabase
    const weekKeys = await getAllWeekKeys();
    const snapshots: DashboardSnapshot[] = [];
    
    // For each week, get all section data
    for (const weekKey of weekKeys) {
      const weekData = await getSnapshotsByWeek(weekKey);
      if (weekData) {
        snapshots.push({
          week: weekKey,
          data: weekData,
          timestamp: new Date().toISOString() // We'll use current time as fallback
        });
      }
    }
    
    if (import.meta.env.DEV) {
      logger.debug('getAllSnapshots - Supabase snapshots', { count: snapshots.length }, 'useSavedSnapshots');
    }
    return snapshots;
  } catch (error) {
    logger.error('getAllSnapshots error', { error }, 'useSavedSnapshots');
    return [];
  }
}


export function useSavedSnapshots() {
  // Track the current week being viewed (null = live/current week)
  const [currentSnapshotWeek, setCurrentSnapshotWeek] = useState<string | null>(null);
  // Track if a manual save was triggered
  const [triggerSave, setTriggerSave] = useState<number>(0);
  // Track all snapshots to prevent stale data
  const [allSnapshots, setAllSnapshots] = useState<DashboardSnapshot[]>([]);
  
  // Load initial snapshots
  useEffect(() => {
    const loadInitialSnapshots = async () => {
      const snapshots = await getAllSnapshots();
      if (import.meta.env.DEV) {
        logger.debug('Initial snapshots loaded', { count: snapshots.length }, 'useSavedSnapshots');
      }
      setAllSnapshots(snapshots);
    };
    
    loadInitialSnapshots();
  }, []);

  // Helper: get all snapshots (memoized)
  const getSnapshots = useCallback(() => {
    if (import.meta.env.DEV) {
      logger.debug('getSnapshots called', { count: allSnapshots.length }, 'useSavedSnapshots');
    }
    return allSnapshots;
  }, [allSnapshots]);

  // Get a snapshot by week string (memoized)
  const getSnapshotByWeek = useCallback(async (week: string): Promise<DashboardSnapshot | undefined> => {
    try {
      const weekData = await getSnapshotsByWeek(week);
      if (weekData) {
        const snapshot: DashboardSnapshot = {
          week,
          data: weekData,
          timestamp: new Date().toISOString()
        };
        
        if (import.meta.env.DEV) {
          logger.debug('getSnapshotByWeek', { week, found: true, sectionCount: Object.keys(snapshot.data).length }, 'useSavedSnapshots');
        }
        
        return snapshot;
      }
      
      if (import.meta.env.DEV) {
        logger.debug('getSnapshotByWeek', { week, found: false }, 'useSavedSnapshots');
      }
      return undefined;
    } catch (error) {
      logger.error('getSnapshotByWeek error', { error, week }, 'useSavedSnapshots');
      return undefined;
    }
  }, []);

  // Save a new snapshot for the given week (MERGE by section, not replace)
  const saveSnapshot = useCallback(async (week: string, partialData: Record<string, unknown>) => {
    if (import.meta.env.DEV) {
      logger.debug('saveSnapshot called (merge mode)', { 
        week, 
        sectionKeys: Object.keys(partialData),
        sectionCount: Object.keys(partialData).length
      }, 'useSavedSnapshots');
    }
    
    // Validate weekKey
    if (!week || typeof week !== 'string') {
      logger.error('Invalid weekKey', { week }, 'useSavedSnapshots');
      return;
    }
    
    try {
      // Save each section to Supabase (only the sections we're updating)
      for (const [sectionKey, sectionData] of Object.entries(partialData)) {
        await saveSnapshotToSupabase(week, sectionKey, sectionData);
      }
      
      // Update local state by refreshing from Supabase
      const updatedSnapshots = await getAllSnapshots();
      setAllSnapshots(updatedSnapshots);
      
      // ✅ Refresh week keys in NavBar dropdown (event-driven refresh after save)
      if (typeof window !== 'undefined' && (window as any).__refreshWeekKeys) {
        try {
          await (window as any).__refreshWeekKeys();
        } catch (error) {
          // Non-critical - dropdown will refresh on next interaction
          if (import.meta.env.DEV) {
            logger.debug('Failed to refresh week keys after save', { error, week }, 'useSavedSnapshots');
          }
        }
      }
      
      if (import.meta.env.DEV) {
        logger.debug('saveSnapshot completed successfully (merged)', { week }, 'useSavedSnapshots');
      }
    } catch (error) {
      logger.error('saveSnapshot error', { error, week }, 'useSavedSnapshots');
    }
  }, []);

  // Centralized save function for current week - ensures correct weekKey usage
  const saveCurrentWeek = useCallback(async (sectionKey: string, data: unknown, weekKey?: string) => {
    const currentWeek = getCurrentWeek();
    if (import.meta.env.DEV) {
      logger.debug('saveCurrentWeek called', {
        sectionKey,
        currentWeek,
        weekKey,
        currentSnapshotWeek
      }, 'useSavedSnapshots');
    }
    
    // Use provided weekKey or fallback to current week
    const weekToSave = weekKey || currentWeek;
    
    if (import.meta.env.DEV) {
      logger.debug('saveCurrentWeek - saving to week', { weekToSave, sectionKey }, 'useSavedSnapshots');
    }
    saveSnapshot(weekToSave, { [sectionKey]: data });
    
    return { success: true, week: weekToSave };
  }, [saveSnapshot, currentSnapshotWeek]);

  // Save function for specific week - allows saving to selected week
  const saveForWeek = useCallback(async (weekKey: string, sectionKey: string, data: unknown) => {
    if (import.meta.env.DEV) {
      logger.debug('saveForWeek called', { weekKey, sectionKey }, 'useSavedSnapshots');
    }
    
    saveSnapshot(weekKey, { [sectionKey]: data });
    
    return { success: true, week: weekKey };
  }, [saveSnapshot]);

  // Helper function to get current week (using standardized utility)
  const getCurrentWeek = useCallback(() => {
    return getCurrentWeekKey();
  }, []);

  // Trigger immediate save from all components
  const triggerImmediateSave = useCallback(() => {
    setTriggerSave(prev => prev + 1);
  }, []);

  // Switch to a specific week (or null for live)
  const switchToWeek = useCallback((week: string | null) => {
    setCurrentSnapshotWeek(week);
  }, []);

  // Delete a snapshot by week
  const deleteSnapshot = useCallback(async (week: string) => {
    try {
      // ✅ PART 3: Delete all sections for this week from Supabase
      const success = await deleteSnapshotsByWeek(week);
      
      if (success) {
        // ✅ PART 3: Clear LocalCache for this week to ensure sections reload fresh data
        try {
          const { Cache } = await import('../data/cache/LocalCache');
          Cache.clearWeek(week);
        } catch (cacheError) {
          // Non-critical - cache will expire naturally
          if (import.meta.env.DEV) {
            logger.debug('Failed to clear cache on delete', { error: cacheError, week }, 'useSavedSnapshots');
          }
        }
        
        // ✅ PART 3: Update local state by refreshing from Supabase (no page reload)
        const updatedSnapshots = await getAllSnapshots();
        setAllSnapshots(updatedSnapshots);
        
        // If deleting the current snapshot, revert to live
        setCurrentSnapshotWeek(prev => (prev === week ? null : prev));
        
        // ✅ PART 3: Trigger event-driven refresh instead of page reload
        // Dispatch event to refresh week keys in NavBar
        window.dispatchEvent(new CustomEvent('marlo:snapshots:weeks:refresh'));
        
        // ✅ PART 3: No window.location.reload() - optimistic UI update
        // All useSimpleSnapshot hooks will detect rowExists=false on next access
      } else {
        logger.error('Failed to delete snapshot', { week }, 'useSavedSnapshots');
      }
    } catch (error) {
      logger.error('deleteSnapshot error', { error, week }, 'useSavedSnapshots');
    }
  }, []);

  // Is the dashboard currently showing a past snapshot?
  const isViewingPastSnapshot = !!currentSnapshotWeek;


  // Get or create snapshot with data inheritance from current week for future weeks
  const getOrCreateSnapshotForWeek = useCallback(async (week: string): Promise<DashboardSnapshot> => {
    if (import.meta.env.DEV) {
      logger.debug('getOrCreateSnapshotForWeek called', { week }, 'useSavedSnapshots');
    }
    
    try {
      // Check if snapshot already exists
      const existingSnapshot = await getSnapshotByWeek(week);
      if (existingSnapshot) {
        if (import.meta.env.DEV) {
          logger.debug('Found existing snapshot for week', { week }, 'useSavedSnapshots');
        }
        return existingSnapshot;
      }
      
      // ✅ ONLY fill forward - never backwards
      // Determine if this is a future week for forward-fill
      // Use date comparison instead of string comparison for reliability (handles week 52 -> 1 transitions)
      const currentWeekKey = getCurrentWeekKey();
      const currentDate = getWeekStartDateFromKey(currentWeekKey);
      const targetDate = getWeekStartDateFromKey(week);
      const isFutureWeek = currentDate && targetDate && targetDate.getTime() > currentDate.getTime();
      
      let inheritFromSnapshot: DashboardSnapshot | undefined;
      
      if (isFutureWeek) {
        // ✅ For future weeks only: inherit from current week specifically
        inheritFromSnapshot = await getSnapshotByWeek(currentWeekKey);
        if (import.meta.env.DEV) {
          logger.debug('Future week detected, inheriting from current week', { currentWeekKey, futureWeek: week }, 'useSavedSnapshots');
        }
      } else {
        // ❌ For past weeks: DO NOT fill backwards - return empty snapshot
        // This prevents accidentally overwriting historical data
        if (import.meta.env.DEV) {
          logger.debug('Past week detected, returning empty snapshot (no backwards fill)', { week, currentWeekKey }, 'useSavedSnapshots');
        }
        return {
          week,
          data: {},
          timestamp: new Date().toISOString()
        };
      }
      
      // Only create snapshot if we have data to inherit (future weeks only)
      if (!inheritFromSnapshot) {
        if (import.meta.env.DEV) {
          logger.debug('No current week data to inherit, returning empty snapshot', { week }, 'useSavedSnapshots');
        }
        return {
          week,
          data: {},
          timestamp: new Date().toISOString()
        };
      }
      
      // Create new snapshot with deeply cloned inherited data to prevent reference sharing
      const newSnapshot: DashboardSnapshot = {
        week,
        data: JSON.parse(JSON.stringify(inheritFromSnapshot.data)), // Deep clone to prevent reference sharing
        timestamp: new Date().toISOString()
      };
      
      if (import.meta.env.DEV) {
        logger.debug('Created new snapshot with forward-filled data', {
          newWeek: week,
          inheritedFrom: inheritFromSnapshot.week,
          isFutureWeek,
          sectionCount: Object.keys(newSnapshot.data).length
        }, 'useSavedSnapshots');
      }
      
      // Save the new snapshot to Supabase (only for future weeks)
      await saveSnapshot(week, newSnapshot.data);
      
      // Update local state
      const updatedSnapshots = [...allSnapshots, newSnapshot];
      setAllSnapshots(updatedSnapshots);
      
      return newSnapshot;
    } catch (error) {
      logger.error('getOrCreateSnapshotForWeek error', { error, week }, 'useSavedSnapshots');
      // Return empty snapshot on error
      return {
        week,
        data: {},
        timestamp: new Date().toISOString()
      };
    }
  }, [allSnapshots, getSnapshotByWeek, saveSnapshot]);

  return {
    getSnapshots,
    getSnapshotByWeek,
    getOrCreateSnapshotForWeek,
    saveSnapshot,
    saveCurrentWeek,
    saveForWeek,
    triggerImmediateSave,
    triggerSave,
    switchToWeek,
    deleteSnapshot,
    currentSnapshotWeek,
    isViewingPastSnapshot,
  };
} 